import Toast from 'react-native-toast-message'
import { getCurrentOrgID } from '../data/organization';
import ErrorHandler from '../utils/ErrorHandler';
import { supabase } from '../data/client';
import { insertActivity } from './activity';
import { buildUpdateDetails } from '../utils/buildActivityDetails';

const getLocations = async () => {
    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.from('Locations').select('*').eq('organizationID', orgID).eq('isDeleted', false).order('name').order('detailedArea');

    if(error){
        ErrorHandler(Error(error?.message), {query: "getLocations", input: {organizationID: orgID, error}}, `Something went wrong fetching locations`)
        throw Error()
    }

    return data;
}

const getLocation = async (id) => {
    if (!id || id === 'undefined' || id === 'null') return null;

    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.from('Locations').select('*, Address:Addresses(*)').eq('organizationID', orgID).eq('isDeleted', false).eq('id', id).maybeSingle();


    if(error){
        ErrorHandler(Error(error?.message), {query: "getLocation", input: {organizationID: orgID, error}}, `Something went wrong fetching location`)
        throw Error()
    }

    return data;
}


const saveLocation = async ({details, originalValues}) => {
    const orgID = await getCurrentOrgID();

    let location = {
        id: details.id,
        name: details.name,
        detailedArea: details.detailedArea,
        addressID: details.addressID || null,
        organizationID: orgID,
    }

    if(details.id){
        return updateLocation(location, originalValues)
    }
    else{
        return createLocation(location)
    }
}


const createLocation = async (location) => {
    const {error, data} = await supabase.from('Locations').insert(location).select().maybeSingle();

    if(error){
        ErrorHandler(Error(error?.message), {mutation: "createLocation", input: location}, `Something went wrong creating that location`)
        throw Error();
    }

    Toast.show({ type: 'success', text2: `New location has been added successfully!`});

    // Log activity (fire and forget - don't block on this)
    insertActivity({
        subjectType: 'Location',
        subjectID: data.id,
        subjectName: data.name,
        verb: 'create',
    }).catch(() => {});

    return data;
}


const updateLocation = async (location, originalValues = null) => {
    const {error, data} = await supabase.from('Locations').update(location).eq('id', location.id).select().maybeSingle();

    if(error){
        ErrorHandler(Error(error?.message), {mutation: "updateLocation", input: location}, `Something went wrong updating that location`)
        throw Error();
    }

    Toast.show({ type: 'success', text2: `Location has been updated successfully!`});

    // Log activity (fire and forget). addressID is an FK and is omitted.
    const details = originalValues ? buildUpdateDetails(originalValues, location, {
        name: { label: 'Name' },
        detailedArea: { label: 'Description' },
    }) : null;

    insertActivity({
        subjectType: 'Location',
        subjectID: data.id,
        subjectName: data.name,
        verb: 'update',
        details,
    }).catch(() => {});

    return data;
}


const deleteLocation = async (locationID) => {
    //Only mark as deleted, don't actually delete
    const input = {
        isDeleted: true
    }

    // Fetch the location name before deleting for the activity log
    const {data: existing} = await supabase.from('Locations').select('name').eq('id', locationID).maybeSingle();

    const {error, data} = await supabase.from('Locations').update(input).eq('id', locationID);

    if(error){
        ErrorHandler(Error(error?.message), {mutation: "deleteLocation", input: input}, `Something went wrong deleting that location`)
        throw Error();
    }

    Toast.show({ type: 'success', text2: `Location has been deleted successfully!`});

    // Log activity (fire and forget - don't block on this)
    insertActivity({
        subjectType: 'Location',
        subjectID: locationID,
        subjectName: existing?.name,
        verb: 'delete',
    }).catch(() => {});

    return data;
}


/**
 * Count distinct non-canceled future EventInstances that occupy a location —
 * via per-instance overrides (EventInstanceLocations), the legacy
 * EventInstances.locationID field, the series' defaultLocationID, or the
 * series' EventLocations join. Used to warn before deleting a location that
 * still has upcoming bookings.
 *
 * @param {string} locationID
 * @returns {Promise<number>}
 */
const getFutureLocationUsageCount = async (locationID) => {
    if(!locationID) return 0;

    const now = new Date().toISOString();
    const instanceIDs = new Set();

    const collect = ({data, error}, tag) => {
        if(error){
            ErrorHandler(Error(error.message), {query: "getFutureLocationUsageCount", input: {locationID, source: tag}, error});
            return;
        }
        (data || []).forEach(row => {
            const id = row?.id || row?.EventInstance?.id;
            if(id) instanceIDs.add(id);
        });
    };

    const results = await Promise.all([
        // Instance-level location overrides
        supabase.from('EventInstanceLocations')
            .select('EventInstance:EventInstances!inner(id)')
            .eq('locationID', locationID)
            .eq('EventInstance.isDeleted', false)
            .eq('EventInstance.canceled', false)
            .gte('EventInstance.startTime', now),
        // Legacy instance.locationID field
        supabase.from('EventInstances')
            .select('id')
            .eq('locationID', locationID)
            .eq('isDeleted', false)
            .eq('canceled', false)
            .gte('startTime', now),
        // Series defaultLocationID
        supabase.from('EventInstances')
            .select('id, Event:Events!inner(id)')
            .eq('Event.defaultLocationID', locationID)
            .eq('Event.isDeleted', false)
            .eq('isDeleted', false)
            .eq('canceled', false)
            .gte('startTime', now),
        // Series EventLocations join rows
        supabase.from('EventInstances')
            .select('id, Event:Events!inner(id, EventLocations!inner(locationID))')
            .eq('Event.EventLocations.locationID', locationID)
            .eq('Event.isDeleted', false)
            .eq('isDeleted', false)
            .eq('canceled', false)
            .gte('startTime', now),
    ]);

    results.forEach((r, i) => collect(r, i));

    return instanceIDs.size;
}


export { 
    getLocations,
    getLocation,
    saveLocation,
    deleteLocation,
    getFutureLocationUsageCount
}